import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { trackProductView } from "@/lib/attribution";
import Seo from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import { useFeatures } from "@/lib/features";
import { ShopBuyModal } from "@/components/ShopBuyModal";
import { productImg } from "@/lib/img";
import { ProductDetails } from "@/components/product/ProductSections";

const SITE = "https://voluntoldhq.com";
const AUD = { coach: "Coaches", parent: "Parents", player: "Players" };
const absImg = (u) => (u && u.startsWith("/") ? `${SITE}${u}` : u);

function productLd(p) {
  return {
    "@context": "https://schema.org", "@type": "Product", name: p.name, description: p.blurb, image: absImg(p.image), sku: p.lookup_key,
    brand: { "@type": "Brand", name: "Voluntold" },
    offers: { "@type": "Offer", price: (p.amount / 100).toFixed(2), priceCurrency: "USD", availability: "https://schema.org/InStock",
      url: `${SITE}/shop/${p.lookup_key}` },
  };
}

export default function ProductPage() {
  const { id } = useParams();
  const features = useFeatures();
  const { search } = useLocation();
  const [p, setP] = useState(undefined);
  const [all, setAll] = useState([]);
  const [buying, setBuying] = useState(false);
  const redirect = id === "starter_kit" ? "/starter-kit" : id === "speech_vault" ? "/speech-vault" : (id === "coach_ready" || (id === "coaching_kit" && features?.coach_ready)) ? "/coach-ready" : id.startsWith("quick_play_") ? `/quick-plays/${id.slice(11).replace(/_/g, "-")}` : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (redirect) return;
    api.get("/products").then(({ data }) => { setAll(data || []); setP((data || []).find((x) => x.lookup_key === id) || null); }).catch(() => setP(null));
  }, [id, redirect]);

  useEffect(() => {
    if (!p) return;
    trackProductView(api, p.lookup_key);
    trackEvent("view_item", { currency: "USD", value: p.amount / 100, items: [{ item_id: p.lookup_key, item_name: p.name }] });
  }, [p]);

  if (redirect) return <Navigate to={`${redirect}${search}`} replace />;
  if (p === null) return <NotFound />;
  if (!p) return <div className="max-w-5xl mx-auto px-4 pt-16"><div className="h-96 border-2 border-[#D3CFC0] animate-pulse" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16" data-testid="product-page">
      <Seo title={p.name} description={p.blurb} path={`/shop/${p.lookup_key}`} image={absImg(p.image)} type="product" jsonLd={productLd(p)} />
      <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-1 kicker text-[#6E6E6E] hover:text-[#1E1E1E]"><ArrowLeft size={14} /> Shop</Link>
      <div className="mt-6 grid lg:grid-cols-[1fr_1fr] gap-6 lg:gap-10 items-start">
        <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] aspect-[4/3] max-h-[230px] lg:max-h-none w-full flex items-center justify-center">
          <img {...productImg(p.image, "(min-width: 1024px) 50vw, 100vw")} alt={p.name} fetchpriority="high" className="w-full h-full object-contain p-3 lg:p-6" data-testid="product-image" />
        </div>
        <div>
          <span className="kicker text-[#DB9130]">{p.line ? (p.is_bundle ? `${p.category_tag} · Bundle` : p.category_tag) : p.is_bundle ? "Bundle" : p.audience.map((a) => AUD[a] || a).join(" · ")}</span>
          <h1 className="mt-2 font-head font-semibold uppercase text-3xl sm:text-5xl text-[#1E1E1E] leading-[1.02]" data-testid="product-name">{p.name}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-5">
            <span className="font-head text-4xl text-[#1E1E1E]" data-testid="product-price">{p.free ? "Free" : `$${p.amount / 100}`}</span>
            {p.separate_amount && <span className="text-lg text-[#6E6E6E]" data-testid="product-separate-price">Separately <s>${p.separate_amount / 100}</s></span>}
            {p.free ? (
              <Link to="/cheat-sheet" data-testid="product-free-download-btn"
                className="bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-8 py-3.5 hover:bg-[#D6922C] transition-colors">
                Download free
              </Link>
            ) : (
            <button onClick={() => setBuying(true)} data-testid="product-buy-btn"
              className="bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-8 py-3.5 hover:bg-[#D6922C] transition-colors">
              Buy Now
            </button>
            )}
          </div>
          <p className="mt-3 text-xs text-[#6E6E6E]">{p.free ? "Free with your email. The PDF arrives right away." : "Instant digital download. Secure checkout via Stripe; your receipt and download link are emailed."}</p>
          {!p.free && (p.in_bundles || []).map((b) => (
            <p key={b.lookup_key} className="mt-3 text-sm text-[#1E1E1E]" data-testid={`product-save-${b.lookup_key}`}>
              Save with the <Link to={`/shop/${b.lookup_key}`} className="font-bold underline underline-offset-4 hover:text-[#DB9130]">{b.name}</Link>: {b.includes.join(", ")} for ${b.amount / 100}.
            </p>
          ))}
          <p className="mt-6 pt-6 border-t-2 border-[#D3CFC0] text-[#3A3A3A] text-base leading-relaxed max-w-xl" data-testid="product-blurb">{p.blurb}</p>
        </div>
      </div>
      <ProductDetails p={p} products={all} />
      {buying && <ShopBuyModal product={p} source="product_page" onClose={() => setBuying(false)} />}
    </div>
  );
}
